import type { EmojiSuggestion } from "#features/conversations/components/conversation-input/emojiSuggestion.ts";
import { StyledSystemProvider, useColorSchemeContext } from "@hopper-ui/styled-system";
import classNames from "classnames";
import { createPortal } from "react-dom";
import "./ComposerEmojiMenu.css";
import "./ComposerEmojiMenu.dark.css";

interface Props {
    suggestion: EmojiSuggestion;
    listboxId: string;
    optionId: (index: number) => string;
    label: string;
}

// The emojis a ":" can insert, drawn as wl-app's editor draws them, at the caret. Focus stays in the editor, which drives the
// keys and points at the active row through aria-activedescendant. The caret's element is outside the HopperProvider, so
// the list is re-wrapped for the color scheme, as the portaled toasts are.
export function ComposerEmojiMenu({ suggestion, listboxId, optionId, label }: Props) {
    const { colorScheme } = useColorSchemeContext();
    const { element, matches, activeIndex, select, setActiveIndex } = suggestion;

    return createPortal(
        <StyledSystemProvider theme="workleap" colorScheme={colorScheme}>
            <div id={listboxId} role="listbox" aria-label={label} className="wai-emoji-menu">
                {matches.map((match, index) => (
                    <div
                        key={match.emoji}
                        id={optionId(index)}
                        role="option"
                        aria-selected={index === activeIndex}
                        tabIndex={-1}
                        className={classNames("wai-emoji-menu-option", { "wai-emoji-menu-option--active": index === activeIndex })}
                        // Picked on a primary press, before the editor would lose focus to the row; another button opens its menu.
                        onMouseDown={event => {
                            if (event.button !== 0) {
                                return;
                            }
                            event.preventDefault();
                            select(match);
                        }}
                        // Not on enter: Chrome fires that on the row under a resting pointer when the list opens, with no movement.
                        onPointerMove={() => setActiveIndex(index)}
                    >
                        <span className="wai-emoji-menu-emoji">{match.emoji}</span>
                        <span className="wai-emoji-menu-shortcode">{match.shortcode}</span>
                    </div>
                ))}
            </div>
        </StyledSystemProvider>,
        element
    );
}
